/**
 * Error Handling Utilities
 *
 * Centralized API error extraction, humanization, and sanitization
 * for consistent, user-friendly messages across the application.
 */

import { isAxiosError } from 'axios';

export interface ApiErrorResponse {
  statusCode: number;
  error?: string;
  message: string | string[];
  path?: string;
  timestamp?: string;
  retryAfter?: number;
}

const GENERIC_SERVER_ERROR =
  'Something went wrong on our end. Please try again later.';
const GENERIC_CLIENT_ERROR =
  'We could not complete your request. Please check your input and try again.';
const GENERIC_UNEXPECTED_ERROR =
  'An unexpected error occurred. Please try again.';

const FIELD_LABELS: Record<string, string> = {
  firstName: 'First name',
  lastName: 'Last name',
  fullName: 'Full name',
  email: 'Email',
  password: 'Password',
  confirmPassword: 'Confirm password',
  currentPassword: 'Current password',
  newPassword: 'New password',
  phone: 'Phone number',
  username: 'Username',
};

const TECHNICAL_MESSAGE_PATTERNS: RegExp[] = [
  /stack trace/i,
  /\bat\s+[\w./]+:\d+/,
  /ECONNREFUSED|ECONNABORTED|ENOTFOUND|ETIMEDOUT/,
  /QueryFailedError|TypeORM|Prisma|Sequelize/i,
  /duplicate key value/i,
  /\bSQL\b/i,
  /internal server error/i,
  /node_modules/i,
  /undefined is not/i,
  /cannot read propert/i,
];

function humanizeFieldName(field: string): string {
  if (FIELD_LABELS[field]) return FIELD_LABELS[field];

  return field
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/^\w/, (char) => char.toUpperCase());
}

/**
 * Converts class-validator / NestJS validation messages to plain language.
 */
export function humanizeValidationMessage(message: string): string {
  const trimmed = message.trim();
  if (!trimmed) return trimmed;

  let match = trimmed.match(
    /^(\w+)\s+must be longer than or equal to (\d+)\s+characters?$/i,
  );
  if (match) {
    return `${humanizeFieldName(match[1])} must be at least ${match[2]} characters`;
  }

  match = trimmed.match(
    /^(\w+)\s+must be shorter than or equal to (\d+)\s+characters?$/i,
  );
  if (match) {
    return `${humanizeFieldName(match[1])} must be at most ${match[2]} characters`;
  }

  match = trimmed.match(/^(\w+)\s+must be an email$/i);
  if (match) {
    return 'Please enter a valid email address';
  }

  match = trimmed.match(/^(\w+)\s+should not be empty$/i);
  if (match) {
    return `${humanizeFieldName(match[1])} is required`;
  }

  match = trimmed.match(/^(\w+)\s+must be a string$/i);
  if (match) {
    return `${humanizeFieldName(match[1])} must be text`;
  }

  match = trimmed.match(/^(\w+)\s+must match .+ regular expression$/i);
  if (match) {
    return `${humanizeFieldName(match[1])} format is invalid`;
  }

  match = trimmed.match(/^property (\w+) should not exist$/i);
  if (match) {
    return `${humanizeFieldName(match[1])} is not allowed`;
  }

  if (/^password.*(weak|strong)/i.test(trimmed)) {
    return 'Password must be at least 8 characters with uppercase, lowercase, and numbers';
  }

  if (/^email already exists$/i.test(trimmed)) {
    return 'An account with this email already exists';
  }

  return trimmed;
}

/**
 * Strips technical or sensitive details from error messages shown to users.
 */
export function sanitizeErrorMessage(message: string): string {
  const trimmed = message.trim();
  if (!trimmed) return GENERIC_UNEXPECTED_ERROR;

  for (const pattern of TECHNICAL_MESSAGE_PATTERNS) {
    if (pattern.test(trimmed)) {
      return GENERIC_SERVER_ERROR;
    }
  }

  return trimmed;
}

function normalizeMessages(
  messages: string[],
  statusCode?: number,
): string[] {
  if (statusCode !== undefined && statusCode >= 500) {
    return [GENERIC_SERVER_ERROR];
  }

  const normalized = messages
    .map((message) => humanizeValidationMessage(message))
    .map((message) => sanitizeErrorMessage(message))
    .filter(Boolean);

  return normalized.length > 0 ? normalized : [GENERIC_UNEXPECTED_ERROR];
}

function messagesFromApiPayload(
  data: ApiErrorResponse | undefined,
  statusCode?: number,
): string[] {
  if (!data) return [];

  if (data.message) {
    const rawMessages = Array.isArray(data.message)
      ? data.message
      : [data.message];

    const normalized = normalizeMessages(
      rawMessages.filter((message) => typeof message === 'string'),
      statusCode ?? data.statusCode,
    );

    if (normalized.length > 0 && normalized[0] !== GENERIC_UNEXPECTED_ERROR) {
      return normalized;
    }
  }

  if (data.error) {
    const mapped = getErrorByCode(data.error);
    if (mapped !== getErrorByCode('UNKNOWN_ERROR')) {
      return [mapped];
    }
  }

  if (statusCode === 404) {
    return ['The requested resource was not found.'];
  }

  if (statusCode === 403) {
    return ["You don't have permission to access this resource."];
  }

  if (statusCode === 401) {
    return ['Your session has expired. Please sign in again.'];
  }

  if (statusCode !== undefined && statusCode >= 400 && statusCode < 500) {
    return [GENERIC_CLIENT_ERROR];
  }

  if (statusCode !== undefined && statusCode >= 500) {
    return [GENERIC_SERVER_ERROR];
  }

  return [];
}

function messagesFromUnknownError(error: unknown): string[] {
  if (Array.isArray(error)) {
    return normalizeMessages(error.filter((item) => typeof item === 'string'));
  }

  if (typeof error === 'string') {
    return normalizeMessages([error]);
  }

  if (isAxiosError<ApiErrorResponse>(error)) {
    const statusCode = error.response?.status;
    const payloadMessages = messagesFromApiPayload(
      error.response?.data,
      statusCode,
    );

    if (payloadMessages.length > 0) {
      return payloadMessages;
    }

    if (error.message) {
      return normalizeMessages([getFriendlyErrorMessage(error.message)]);
    }
  }

  if (error instanceof Error && error.message) {
    return normalizeMessages([getFriendlyErrorMessage(error.message)]);
  }

  return [];
}

/**
 * Returns all user-facing error messages for an API/unknown error.
 */
export function getErrorMessages(error: unknown): string[] {
  const messages = messagesFromUnknownError(error);
  return messages.length > 0 ? messages : [GENERIC_UNEXPECTED_ERROR];
}

/**
 * Formats any error into a single user-facing string.
 */
export function formatErrorMessage(error: unknown): string {
  return getErrorMessages(error).join('. ');
}

/**
 * Preferred helper when a caller-specific fallback is needed.
 */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  const messages = messagesFromUnknownError(error);
  if (messages.length === 0) return fallback;

  const formatted = messages.join('. ');
  if (formatted === GENERIC_UNEXPECTED_ERROR) return fallback;
  return formatted;
}

/**
 * Converts technical transport errors to user-friendly ones.
 */
export function getFriendlyErrorMessage(technicalMessage: string): string {
  const friendlyMessages: Record<string, string> = {
    'Network Error':
      'Unable to connect to the server. Please check your internet connection.',
    timeout: 'The request took too long. Please try again.',
    ECONNABORTED: 'Connection interrupted. Please try again.',
    ERR_NETWORK: 'Network error. Please check your connection.',
    ERR_CANCELED: 'Request was cancelled.',
  };

  for (const [key, value] of Object.entries(friendlyMessages)) {
    if (technicalMessage.includes(key)) {
      return value;
    }
  }

  return technicalMessage;
}

/**
 * Error code to user-friendly message mapping.
 */
export function getErrorByCode(errorCode: string): string {
  const errorMessages: Record<string, string> = {
    LOGIN_FAILED: 'Email or password is incorrect. Please try again.',
    PASSWORD_NOT_SET:
      'No password is set yet. Check your email for the activation link, or request a new one below.',
    USER_NOT_FOUND: 'No account found with this email address.',
    INVALID_CREDENTIALS: 'Your login credentials are incorrect.',
    EMAIL_ALREADY_EXISTS: 'An account with this email already exists.',
    USER_ALREADY_EXISTS:
      'An account with this email already exists. Sign in, or set your password if you checked out as a guest.',
    INVALID_TOKEN: 'Your session has expired. Please sign in again.',
    TOKEN_EXPIRED: 'Your session has expired. Please sign in again.',
    UNAUTHORIZED: 'You need to be signed in to access this resource.',
    VALIDATION_ERROR: 'Please check your input and try again.',
    INVALID_EMAIL: 'Please enter a valid email address.',
    WEAK_PASSWORD:
      'Password must be at least 8 characters with uppercase, lowercase, and numbers.',
    PASSWORD_MISMATCH: 'Passwords do not match.',
    NETWORK_ERROR: 'Unable to connect. Please check your internet connection.',
    SERVER_ERROR: GENERIC_SERVER_ERROR,
    TIMEOUT: 'Request timed out. Please try again.',
    NOT_FOUND: 'The requested resource was not found.',
    FORBIDDEN: "You don't have permission to access this resource.",
    BadRequestException: 'Please check your input and try again.',
    UnauthorizedException: 'Your session has expired. Please sign in again.',
    ForbiddenException: "You don't have permission to access this resource.",
    NotFoundException: 'The requested resource was not found.',
    ConflictException: 'This action conflicts with existing data.',
    UNKNOWN_ERROR: GENERIC_UNEXPECTED_ERROR,
  };

  return errorMessages[errorCode] || errorMessages.UNKNOWN_ERROR;
}

export type ErrorSeverity = 'error' | 'warning' | 'info';

export function getErrorSeverity(statusCode?: number): ErrorSeverity {
  if (!statusCode) return 'error';
  if (statusCode >= 500) return 'error';
  if (statusCode >= 400) return 'warning';
  return 'info';
}

export function isValidationError(error: unknown): boolean {
  if (!isAxiosError<ApiErrorResponse>(error)) return false;

  const { data } = error.response ?? {};
  return (
    data?.error === 'VALIDATION_ERROR' ||
    data?.error === 'BadRequestException' ||
    Array.isArray(data?.message) ||
    (error.response?.status === 400 && Boolean(data?.message))
  );
}

export function getApiErrorStatusCode(error: unknown): number | undefined {
  if (isAxiosError<ApiErrorResponse>(error)) {
    return error.response?.status ?? error.response?.data?.statusCode;
  }
  return undefined;
}
